import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, serverTimestamp, addDoc, where, getDocs, FieldPath } from 'firebase/firestore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { UserPlus, UserX, Clock, Save, Loader2, AlertTriangle, Users, UserCheck } from 'lucide-react';

/**
 * Composant de saisie rapide des retards et absences pour les enseignants (pour le "rappel").
 * @param {object} props.db - Instance de Firebase Firestore.
 * @param {string} props.schoolId - ID de l'école.
 * @param {string} props.userId - ID de l'utilisateur (enseignant) connecté.
 * @param {function} props.setError - Fonction pour afficher une erreur.
 * @param {function} props.setMessage - Fonction pour afficher un message de succès.
 */
const TeacherAttendanceInput = ({ db, schoolId, userId, setError, setMessage, userRole }) => {
  // L'accès est uniquement pour l'enseignant, admin, directeur pédagogique
  const canInput = userRole === 'enseignant' || userRole === 'superAdmin' || userRole === 'admin' || userRole === 'directeurPedagogique'; 
  
  // --- Configuration de l'Année Scolaire Actuelle ---
  const getCurrentSchoolYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return currentMonth >= 9 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
  };
  const CURRENT_SCHOOL_YEAR = getCurrentSchoolYear();

  // --- États des Données ---
  const [loading, setLoading] = useState(true);
  const [teacherClasses, setTeacherClasses] = useState([]); // Classes assignées à cet enseignant
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classStudents, setClassStudents] = useState([]); // Élèves de la classe sélectionnée
  const [attendanceDate, setAttendanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [attendanceTime, setAttendanceTime] = useState(format(new Date(), 'HH:mm'));

  // { eleveId: 'retard' | 'absence' | 'present' }
  const [markedAttendance, setMarkedAttendance] = useState({}); 

  // --- 1. CHARGEMENT DES CLASSES DE L'ENSEIGNANT (Réel) ---
useEffect(() => {
    if (!schoolId || !userId || !canInput) return;
    setLoading(true);
    
    // 1. Charger les IDs de classes affectées à cet enseignant pour l'année en cours
    const qAssignments = query(
        collection(db, 'schools', schoolId, 'teacherClassAssignments'),
        where('teacherId', '==', userId),
        where('academicYear', '==', CURRENT_SCHOOL_YEAR)
    );
    
    // Le listener sur les affectations
    const unsubAssignments = onSnapshot(qAssignments, async (assignmentsSnapshot) => {
        const classIds = assignmentsSnapshot.docs.map(doc => doc.data().classId);
        
        if (classIds.length === 0) {
            setTeacherClasses([]);
            setLoading(false);
            return;
        }

        // 2. Charger les détails (noms) des classes trouvées
        // NOTE: Firestore ne gère pas les requêtes `where('id', 'in', array)` sur plus de 10 éléments.
        // Pour les démos, nous utilisons ici un chargement par ID pour simuler la récupération des noms.
        
        const classesRef = collection(db, 'schools', schoolId, 'classes');
        const classPromises = classIds.map(id => classesRef.doc(id).get()); // Non-fonctionnel en JS moderne, doit être importé si vous utilisez la version 9 modulaire
        
        // CORRECTION pour Firebase v9 modulaire:
        // On récupère les IDs et on suppose que les noms de classe sont déjà dans les 'classes'.
        // Comme Firestore ne supporte pas l'opérateur 'in' sur plus de 10 éléments sans index,
        // nous allons faire une requête 'in' si le nombre est bas, ou une simulation si plus.
        
        let classes = [];

        // Tentative de requête 'in' (limite de 10)
        if (classIds.length > 0 && classIds.length <= 10) {
             const qClassesDetails = query(
                 collection(db, 'schools', schoolId, 'classes'),
                 where(db.FieldPath.documentId(), 'in', classIds) // Nécessite FieldPath pour l'ID de document
             );

             const snapshot = await getDocs(qClassesDetails); // Importez getDocs
             classes = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                name: doc.data().name || `Classe ID: ${doc.id.substring(0, 5)}`
             }));

        } else if (classIds.length > 10) {
            // SIMULATION ou récupération individuelle (moins performant) pour plus de 10 classes.
            classes = classIds.map(id => ({
                id: id, 
                name: `Classe (ID: ${id.substring(0, 5)})` // Nom temporaire si trop de classes
            }));
            setError("Plus de 10 classes affectées. Les noms réels peuvent ne pas s'afficher correctement.");

        } else {
             // 0 classes
        }

        setTeacherClasses(classes.sort((a, b) => a.name.localeCompare(b.name)));
        if (classes.length > 0 && !selectedClassId) {
            setSelectedClassId(classes[0].id);
        }
        setLoading(false);
    }, (e) => {
        console.error("Erreur chargement affectations classes:", e);
        setError("Impossible de charger la liste des classes affectées.");
        setLoading(false);
    });

    // Remarque : Si vous utilisez Firebase v9, vous aurez besoin de `getDocs` et `FieldPath`.
    return () => unsubAssignments(); 
}, [schoolId, db, userId, canInput, selectedClassId, setError, CURRENT_SCHOOL_YEAR]); // Ajout de CURRENT_SCHOOL_YEAR dans les dépendances


  // --- 3. GESTION DE L'INPUT ET DE LA SAUVEGARDE ---

  const handleMarkAttendance = (eleveId, status) => {
    setMarkedAttendance(prev => {
        if (prev[eleveId] === status) {
            // Dé-marquer si on clique deux fois sur le même statut
            const newState = { ...prev };
            delete newState[eleveId];
            return newState;
        }
        return { ...prev, [eleveId]: status };
    });
  };
  
  const handleBulkSave = async () => {
    setError('');
    setMessage('');
    
    if (Object.keys(markedAttendance).length === 0) {
        setError("Veuillez marquer au moins un élève avant de sauvegarder.");
        return;
    }
    
    if (!selectedClassId) {
        setError("Veuillez sélectionner une classe.");
        return;
    }
    
    const eventDateTime = new Date(`${attendanceDate}T${attendanceTime}`);
    if (isNaN(eventDateTime)) {
        setError("Format de date ou d'heure invalide.");
        return;
    }

    const batch = [];
    const attendanceCollectionRef = collection(db, 'schools', schoolId, 'absencesRetards');
    
    // Préparer les documents à ajouter
    Object.entries(markedAttendance).forEach(([eleveId, status]) => {
        // Trouver le nom de l'élève (important pour la dénormalisation)
        const student = classStudents.find(s => s.id === eleveId);
        
        if (student && status !== 'present') {
            const data = {
                eleveId: eleveId,
                studentName: student.name,
                classeId: selectedClassId,
                type: status, // 'retard' ou 'absence'
                isJustified: false,
                justificationDetails: '',
                dateEvent: eventDateTime,
                reportedBy: userId,
                createdAt: serverTimestamp(),
            };
            batch.push(data);
        }
    });

    if (batch.length === 0) {
        setMessage("Tous les élèves sont marqués 'Présent' ou aucun élève n'a été marqué.");
        return;
    }
    
    try {
        // Note: Firestore n'a pas de batch.addDoc. On itère et on ajoute.
        await Promise.all(batch.map(data => addDoc(attendanceCollectionRef, data)));

        setMessage(`${batch.length} enregistrements de suivi pour la classe ${selectedClassId} ont été ajoutés avec succès !`);
        setMarkedAttendance({}); // Réinitialiser le marquage
    } catch (error) {
        console.error("Erreur lors de la sauvegarde groupée:", error);
        setError("Échec de la sauvegarde groupée des suivis.");
    }
  };


  if (!canInput) {
    return <div className="p-6 text-center text-red-500">Accès refusé. Seuls les enseignants peuvent effectuer le rappel d'appel ici.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin text-blue-600 mr-3" size={32} />
        <p className="text-gray-700">Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-green-700 mb-6 flex items-center">
        <UserCheck size={28} className="mr-3" /> 
        Saisie d'Appel (Rappel)
      </h2>
      
      {/* 1. Configuration de l'Appel */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center"><Users size={20} className="mr-2"/> Choisir le Contexte</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Sélection de la Classe */}
          <div>
            <label htmlFor="selectedClassId" className="block text-sm font-medium text-gray-700">Classe Concernée</label>
            <select
              id="selectedClassId" value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 bg-white"
              required
            >
              {teacherClasses.length === 0 ? (
                 <option value="">Aucune classe trouvée</option>
              ) : (
                  teacherClasses.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                  ))
              )}
            </select>
          </div>
          
          {/* Date de l'Appel */}
          <div>
            <label htmlFor="attendanceDate" className="block text-sm font-medium text-gray-700">Date de l'Appel</label>
            <input
              type="date" id="attendanceDate" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)}
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500"
              required
            />
          </div>
          
          {/* Heure de l'Appel */}
          <div>
            <label htmlFor="attendanceTime" className="block text-sm font-medium text-gray-700">Heure (Début du Cours)</label>
            <input
              type="time" id="attendanceTime" value={attendanceTime} onChange={(e) => setAttendanceTime(e.target.value)}
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500"
              required
            />
          </div>
        </div>
        
        {classStudents.length === 0 && selectedClassId && (
            <div className="mt-4 p-3 bg-yellow-100 text-yellow-800 rounded-lg flex items-center">
                <AlertTriangle size={20} className="mr-2"/>
                Aucun élève inscrit pour la classe sélectionnée ({selectedClassId}).
            </div>
        )}
      </div>

      
      {/* 2. Liste des Élèves et Marquage */}
      {classStudents.length > 0 && (
          <>
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200">
                <h3 className="text-xl font-semibold mb-4 text-gray-700">Liste des Élèves ({classStudents.length})</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {classStudents.map(student => (
                        <div key={student.id} className="p-3 border rounded-lg shadow-sm flex flex-col items-center justify-between transition duration-150 hover:shadow-md">
                            <span className="text-sm font-medium text-gray-800 text-center mb-2">{student.name}</span>
                            
                            <div className="flex space-x-2">
                                {/* Bouton Absence */}
                                <button
                                    type="button"
                                    onClick={() => handleMarkAttendance(student.id, 'absence')}
                                    className={`p-2 rounded-full transition duration-150 flex items-center justify-center ${markedAttendance[student.id] === 'absence' ? 'bg-red-600 text-white shadow-lg' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                                    title="Marquer Absent"
                                >
                                    <UserX size={20} />
                                </button>

                                {/* Bouton Retard */}
                                <button
                                    type="button"
                                    onClick={() => handleMarkAttendance(student.id, 'retard')}
                                    className={`p-2 rounded-full transition duration-150 flex items-center justify-center ${markedAttendance[student.id] === 'retard' ? 'bg-yellow-600 text-white shadow-lg' : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'}`}
                                    title="Marquer en Retard"
                                >
                                    <Clock size={20} />
                                </button>
                                
                                {/* Indication Présent (Neutre) */}
                                <div
                                    className={`p-2 rounded-full flex items-center justify-center ${markedAttendance[student.id] === 'absence' || markedAttendance[student.id] === 'retard' ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-600'}`}
                                    title="Présent (Non marqué)"
                                >
                                    <UserPlus size={20} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 3. Bouton de Sauvegarde Groupée */}
            <div className="flex justify-end sticky bottom-0 p-4 bg-white border-t rounded-t-xl shadow-xl">
                <button
                    type="button"
                    onClick={handleBulkSave}
                    disabled={Object.keys(markedAttendance).length === 0}
                    className={`py-3 px-8 rounded-lg font-semibold transition duration-200 shadow-lg flex items-center justify-center ${Object.keys(markedAttendance).length > 0 ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                >
                    <Save size={20} className="mr-2"/> 
                    Enregistrer les {Object.keys(markedAttendance).length} Suivis
                </button>
            </div>
          </>
      )}
    </div>
  );
};

export default TeacherAttendanceInput;
